import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EmptyState from "@/components/ui/empty-state";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  emailTemplateService,
  getDefaultBccEmail,
} from "@/services/emailTemplateService";
import { EmailAutocomplete } from "@/components/EmailAutocomplete";
import type { EmailTemplate, TemplateCategory } from "@/types/WeeklyReport";
import { DEFAULT_CATEGORIES, TemplateVisibility } from "@/types/WeeklyReport";
import {
  TemplateCategoryBadge,
  TemplateTagInput,
  TemplateVisibilityBadge,
  ShareTemplateModal,
  VersionHistoryPanel,
} from "@/components/templates";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import {
  Calendar,
  Copy,
  Edit,
  Filter,
  History,
  Mail,
  PlusCircle,
  Search,
  Share2,
  Trash2,
  Users,
  Lock,
  Building2,
  Tag,
  X,
} from "lucide-react";
import React, { useEffect, useState, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import * as z from "zod";

// Form schema for template
const templateSchema = z.object({
  name: z.string().min(1, "Template name is required"),
  subject: z.string().optional(), // Optional - will be set when sending email
  to: z
    .array(z.object({ value: z.string().email("Invalid email address") }))
    .default([]),
  cc: z
    .array(z.object({ value: z.string().email("Invalid email address") }))
    .default([]),
  bcc: z
    .array(z.object({ value: z.string().email("Invalid email address") }))
    .default([]),
  greeting: z.string().optional(),
  signature: z.string().optional(),
  categoryId: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

type TemplateTab = "my-templates" | "shared-with-me" | "organization";

const EmailTemplates = () => {
  const { user } = useAuth();

  // Template state
  const [myTemplates, setMyTemplates] = useState<EmailTemplate[]>([]);
  const [sharedTemplates, setSharedTemplates] = useState<EmailTemplate[]>([]);
  const [orgTemplates, setOrgTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Categories and tags
  const [categories, setCategories] =
    useState<TemplateCategory[]>(DEFAULT_CATEGORIES);
  const [allTags, setAllTags] = useState<string[]>([]);

  // Filters
  const [activeTab, setActiveTab] = useState<TemplateTab>("my-templates");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterTags, setFilterTags] = useState<string[]>([]);

  // Modals
  const [shareModalTemplate, setShareModalTemplate] =
    useState<EmailTemplate | null>(null);
  const [versionPanelTemplate, setVersionPanelTemplate] =
    useState<EmailTemplate | null>(null);

  // Form tags state
  const [formTags, setFormTags] = useState<string[]>([]);

  // Load templates on component mount
  useEffect(() => {
    if (user?.email) {
      loadAllData();
    }
  }, [user]);

  // Load all data (templates, categories, tags)
  const loadAllData = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);

      // Load in parallel
      const [
        userTemplates,
        sharedWithMe,
        orgTemplates,
        categoriesData,
        tagsData,
      ] = await Promise.all([
        emailTemplateService.getTemplatesByUser(user.email),
        emailTemplateService.getTemplatesSharedWithMe(),
        emailTemplateService.getOrganizationTemplates(),
        emailTemplateService.getCategories(),
        emailTemplateService.getAllTags(),
      ]);

      // Validate and set templates
      const validUserTemplates = Array.isArray(userTemplates)
        ? userTemplates.map(normalizeTemplate)
        : [];

      if (validUserTemplates.length === 0) {
        const defaultTemplate =
          await emailTemplateService.createDefaultTemplate(user.email);
        setMyTemplates([defaultTemplate]);
      } else {
        setMyTemplates(validUserTemplates);
      }

      setSharedTemplates(
        Array.isArray(sharedWithMe) ? sharedWithMe.map(normalizeTemplate) : [],
      );
      setOrgTemplates(
        Array.isArray(orgTemplates) ? orgTemplates.map(normalizeTemplate) : [],
      );
      setCategories(
        categoriesData.length > 0 ? categoriesData : DEFAULT_CATEGORIES,
      );
      setAllTags(tagsData.map((tagStat) => tagStat.tag));
    } catch (error) {
      console.error("Error loading data:", error);
      setMyTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const normalizeTemplate = (template: EmailTemplate): EmailTemplate => ({
    ...template,
    to: Array.isArray(template.to) ? template.to : [],
    cc: Array.isArray(template.cc) ? template.cc : [],
    bcc: Array.isArray(template.bcc) ? template.bcc : [],
    name: template.name || "Unnamed Template",
    subject: template.subject || "No Subject",
    updatedAt: template.updatedAt || new Date().toISOString(),
    visibility: template.visibility || "private",
    tags: template.tags || [],
    categoryId: template.categoryId ?? null,
  });

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    let templates: EmailTemplate[] = [];

    switch (activeTab) {
      case "my-templates":
        templates = myTemplates;
        break;
      case "shared-with-me":
        templates = sharedTemplates;
        break;
      case "organization":
        templates = orgTemplates;
        break;
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      templates = templates.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.subject.toLowerCase().includes(query) ||
          t.tags?.some((tag) => tag.toLowerCase().includes(query)),
      );
    }

    // Apply category filter
    if (filterCategory) {
      templates = templates.filter((t) => t.categoryId === filterCategory);
    }

    // Apply tags filter
    if (filterTags.length > 0) {
      templates = templates.filter((t) =>
        filterTags.some((tag) => t.tags?.includes(tag)),
      );
    }

    return templates;
  }, [
    activeTab,
    myTemplates,
    sharedTemplates,
    orgTemplates,
    searchQuery,
    filterCategory,
    filterTags,
  ]);

  // Get category by ID
  const getCategoryById = (
    categoryId: string | null,
  ): TemplateCategory | null => {
    if (!categoryId) return null;
    return categories.find((c) => c.id === categoryId) || null;
  };

  // Setup form
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: "",
      subject: "",
      to: [],
      cc: [],
      bcc: [{ value: getDefaultBccEmail() }],
      greeting:
        "Hi All,\nPlease take a look at the report for this week below.",
      signature: "BR,\nYour Name",
      categoryId: null,
      tags: [],
    },
  });

  // Setup field arrays
  const {
    fields: toFields,
    append: appendTo,
    remove: removeTo,
  } = useFieldArray({
    control: form.control,
    name: "to",
  });

  const {
    fields: ccFields,
    append: appendCc,
    remove: removeCc,
  } = useFieldArray({
    control: form.control,
    name: "cc",
  });

  const {
    fields: bccFields,
    append: appendBcc,
    remove: removeBcc,
  } = useFieldArray({
    control: form.control,
    name: "bcc",
  });

  // Reset form with template data
  const resetFormWithTemplate = (template: EmailTemplate) => {
    form.reset({
      name: template.name,
      subject: template.subject,
      to: template.to.map((email) => ({ value: email })),
      cc: template.cc.map((email) => ({ value: email })),
      bcc: template.bcc.map((email) => ({ value: email })),
      greeting:
        template.greeting ||
        "Hi All,\nPlease take a look at the report for this week below.",
      signature: template.signature || "BR,\nYour Name",
      categoryId: template.categoryId ?? null,
      tags: template.tags || [],
    });
    setFormTags(template.tags || []);
  };

  // Handle creating a new template
  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    form.reset({
      name: "",
      subject: "",
      to: [],
      cc: [],
      bcc: [{ value: getDefaultBccEmail() }],
      greeting:
        "Hi All,\nPlease take a look at the report for this week below.",
      signature: "BR,\nYour Name",
      categoryId: null,
      tags: [],
    });
    setFormTags([]);
    setIsDialogOpen(true);
  };

  // Handle editing a template
  const handleEditTemplate = (template: EmailTemplate) => {
    // Only allow editing own templates
    if (activeTab !== "my-templates" && template.ownerId !== user?.email) {
      toast.error("Cannot edit", {
        description:
          'You can only edit your own templates. Use "Use as Template" to create a copy.',
      });
      return;
    }
    setSelectedTemplate(template);
    resetFormWithTemplate(template);
    setIsDialogOpen(true);
  };

  // Handle copying a shared/org template to private
  const handleCopyToPrivate = async (template: EmailTemplate) => {
    try {
      const newTemplate = await emailTemplateService.copyToPrivate(template.id);
      if (newTemplate) {
        toast.success("Template copied", {
          description: "A copy has been added to your templates",
        });
        await loadAllData();
        setActiveTab("my-templates");
      } else {
        throw new Error("Failed to copy template");
      }
    } catch (error) {
      toast.error("Error", {
        description: "Failed to copy template",
      });
    }
  };

  // Handle saving a template
  const handleSaveTemplate = async (data: TemplateFormValues) => {
    if (!user?.email) return;

    try {
      const nameExists = myTemplates.some(
        (template) =>
          template.name.toLowerCase() === data.name.toLowerCase() &&
          (!selectedTemplate || template.id !== selectedTemplate.id),
      );

      if (nameExists) {
        toast.error("Error", {
          description: "A template with this name already exists.",
        });
        return;
      }

      const templateData: EmailTemplate = {
        id: selectedTemplate?.id || uuidv4(),
        name: data.name,
        subject: data.subject,
        to: data.to.map((item) => item.value),
        cc: data.cc.map((item) => item.value),
        bcc: data.bcc.map((item) => item.value),
        greeting: data.greeting,
        signature: data.signature,
        createdBy: user.email,
        createdAt: selectedTemplate?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        visibility: selectedTemplate?.visibility || "private",
        ownerId: selectedTemplate?.ownerId || user.email,
        sharedWith: selectedTemplate?.sharedWith || [],
        categoryId: data.categoryId ?? null,
        tags: formTags,
        currentVersion: selectedTemplate?.currentVersion || 1,
        versions: selectedTemplate?.versions || [],
      };

      const defaultBcc = getDefaultBccEmail();
      if (!templateData.bcc.includes(defaultBcc)) {
        templateData.bcc.push(defaultBcc);
      }

      await emailTemplateService.saveTemplate(templateData);
      await loadAllData();
      setIsDialogOpen(false);

      toast.success(
        selectedTemplate
          ? "Template updated successfully"
          : "Template created successfully",
      );
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Error", {
        description: "Failed to save template",
      });
    }
  };

  // Handle deleting a template
  const handleDeleteTemplate = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await emailTemplateService.deleteTemplate(id);
      await loadAllData();
      toast.success("Template deleted successfully");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Error", {
        description: "Failed to delete template",
      });
    }
  };

  // Handle version revert
  const handleVersionRevert = (revertedTemplate: EmailTemplate) => {
    const updatedTemplates = myTemplates.map((t) =>
      t.id === revertedTemplate.id ? revertedTemplate : t,
    );
    setMyTemplates(updatedTemplates);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setFilterCategory(null);
    setFilterTags([]);
  };

  const hasActiveFilters =
    searchQuery || filterCategory || filterTags.length > 0;

  // Render template card
  const renderTemplateCard = (
    template: EmailTemplate,
    index: number,
    isOwner: boolean,
  ) => {
    const category = getCategoryById(template.categoryId);

    return (
      <Card
        key={template.id}
        className="group cursor-pointer hover:border-primary/50 transition-all duration-200 animate-slide-up"
        style={{ animationDelay: `${index * 50}ms` }}
        onClick={() =>
          isOwner ? handleEditTemplate(template) : handleCopyToPrivate(template)
        }
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base truncate">
                  {template.name}
                </CardTitle>
                <CardDescription className="text-xs mt-0.5 truncate">
                  {template.subject}
                </CardDescription>
              </div>
            </div>
            <div
              className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              {isOwner && (
                <>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setShareModalTemplate(template)}
                    title="Share"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setVersionPanelTemplate(template)}
                    title="Version history"
                  >
                    <History className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => handleEditTemplate(template)}
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => handleDeleteTemplate(template.id, e)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
              {!isOwner && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleCopyToPrivate(template)}
                  title="Use as template"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {/* Badges row */}
            <div className="flex flex-wrap gap-1.5">
              <TemplateVisibilityBadge
                visibility={template.visibility || "private"}
                size="sm"
              />
              {category && (
                <TemplateCategoryBadge category={category} size="sm" />
              )}
            </div>

            {/* Tags */}
            {template.tags && template.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {template.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    <Tag className="h-2.5 w-2.5 mr-1" />
                    {tag}
                  </Badge>
                ))}
                {template.tags.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{template.tags.length - 3}
                  </Badge>
                )}
              </div>
            )}

            {/* Recipients summary */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                <span>{template.to?.length || 0} TO</span>
              </div>
              <span>•</span>
              <span>{template.cc?.length || 0} CC</span>
              <span>•</span>
              <span>{template.bcc?.length || 0} BCC</span>
            </div>

            {/* Last updated & version */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                <span>
                  Updated {format(new Date(template.updatedAt), "MMM d, yyyy")}
                </span>
              </div>
              {template.currentVersion && (
                <Badge variant="secondary" className="text-xs">
                  v{template.currentVersion}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Email Templates
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage reusable email templates for your reports
          </p>
        </div>
        <Button onClick={handleCreateTemplate} className="gap-2">
          <PlusCircle className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {/* Tabs & Filters */}
      <div className="space-y-4">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TemplateTab)}
        >
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <TabsList>
              <TabsTrigger value="my-templates" className="gap-2">
                <Lock className="h-4 w-4" />
                My Templates
                <Badge variant="secondary" className="ml-1">
                  {myTemplates.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="shared-with-me" className="gap-2">
                <Share2 className="h-4 w-4" />
                Shared with Me
                <Badge variant="secondary" className="ml-1">
                  {sharedTemplates.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="organization" className="gap-2">
                <Building2 className="h-4 w-4" />
                Organization
                <Badge variant="secondary" className="ml-1">
                  {orgTemplates.length}
                </Badge>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={filterCategory || "all"}
              onValueChange={(v) => setFilterCategory(v === "all" ? null : v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: cat.color }}
                      />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="gap-2">
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          {/* Filter Tags */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {allTags.slice(0, 15).map((tag) => (
                <Badge
                  key={tag}
                  variant={filterTags.includes(tag) ? "default" : "outline"}
                  className="cursor-pointer transition-colors"
                  onClick={() => {
                    if (filterTags.includes(tag)) {
                      setFilterTags(filterTags.filter((t) => t !== tag));
                    } else {
                      setFilterTags([...filterTags, tag]);
                    }
                  }}
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Templates Content */}
          <TabsContent value={activeTab} className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-56" />
                ))}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <EmptyState
                    icon={activeTab === "my-templates" ? Mail : Share2}
                    title={
                      hasActiveFilters
                        ? "No templates match your filters"
                        : activeTab === "my-templates"
                          ? "No templates yet"
                          : activeTab === "shared-with-me"
                            ? "No shared templates"
                            : "No organization templates"
                    }
                    description={
                      hasActiveFilters
                        ? "Try adjusting your search or filters"
                        : activeTab === "my-templates"
                          ? "Create your first email template to streamline your weekly reporting workflow."
                          : "Templates shared with you will appear here."
                    }
                    action={
                      hasActiveFilters
                        ? { label: "Clear Filters", onClick: clearFilters }
                        : activeTab === "my-templates"
                          ? {
                              label: "Create Template",
                              onClick: handleCreateTemplate,
                            }
                          : undefined
                    }
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates.map((template, index) =>
                  renderTemplateCard(
                    template,
                    index,
                    activeTab === "my-templates" ||
                      template.ownerId === user?.email,
                  ),
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Template Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate
                ? "Update your email template details"
                : "Create a new email template for your reports"}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSaveTemplate)}
              className="space-y-5 mt-4"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Client ABC Weekly" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Subject Line{" "}
                      <span className="text-muted-foreground font-normal">
                        (optional)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Leave empty to set when sending"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Optional default subject. Can be changed when sending the
                      email.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Category & Tags Section */}
              <div className="space-y-4 p-4 rounded-lg border border-border bg-secondary/20">
                <h4 className="font-medium text-sm">Category & Tags</h4>

                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">
                        Category
                      </FormLabel>
                      <Select
                        value={field.value || "none"}
                        onValueChange={(v) =>
                          field.onChange(v === "none" ? null : v)
                        }
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No Category</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: cat.color }}
                                />
                                {cat.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <FormLabel className="text-xs text-muted-foreground">
                    Tags
                  </FormLabel>
                  <TemplateTagInput
                    tags={formTags}
                    onChange={setFormTags}
                    suggestedTags={allTags.map((tag) => ({
                      tag,
                      count: 1,
                      lastUsed: new Date().toISOString(),
                    }))}
                  />
                </div>
              </div>

              {/* Recipients Section */}
              <div className="space-y-4 p-4 rounded-lg border border-border bg-secondary/20">
                <h4 className="font-medium text-sm">Recipients</h4>

                {/* TO Field */}
                <div className="space-y-2">
                  <FormLabel className="text-xs text-muted-foreground">
                    TO
                  </FormLabel>
                  {toFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name={`to.${index}.value`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <EmailAutocomplete
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Start typing to search contacts..."
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => removeTo(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendTo({ value: "" })}
                  >
                    <PlusCircle className="h-3.5 w-3.5 mr-1" />
                    Add TO
                  </Button>
                </div>

                {/* CC Field */}
                <div className="space-y-2">
                  <FormLabel className="text-xs text-muted-foreground">
                    CC
                  </FormLabel>
                  {ccFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name={`cc.${index}.value`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <EmailAutocomplete
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Start typing to search contacts..."
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => removeCc(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendCc({ value: "" })}
                  >
                    <PlusCircle className="h-3.5 w-3.5 mr-1" />
                    Add CC
                  </Button>
                </div>

                {/* BCC Field */}
                <div className="space-y-2">
                  <FormLabel className="text-xs text-muted-foreground">
                    BCC
                  </FormLabel>
                  {bccFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name={`bcc.${index}.value`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              {field.value === getDefaultBccEmail() ? (
                                <Input
                                  placeholder="email@example.com"
                                  value={field.value}
                                  disabled
                                />
                              ) : (
                                <EmailAutocomplete
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Start typing to search contacts..."
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {form.watch(`bcc.${index}.value`) !==
                        getDefaultBccEmail() && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => removeBcc(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendBcc({ value: "" })}
                  >
                    <PlusCircle className="h-3.5 w-3.5 mr-1" />
                    Add BCC
                  </Button>
                </div>
              </div>

              <FormField
                control={form.control}
                name="greeting"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Greeting</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Hi All,&#10;Please take a look at the report for this week below."
                        {...field}
                        className="h-20 resize-y"
                      />
                    </FormControl>
                    <FormDescription>
                      Text that appears at the start of the email
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="signature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Signature</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="BR,&#10;Your Name"
                        {...field}
                        className="h-20 resize-y"
                      />
                    </FormControl>
                    <FormDescription>
                      Your signature at the end of the email
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {selectedTemplate ? "Save Changes" : "Create Template"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <ShareTemplateModal
        template={shareModalTemplate}
        isOpen={!!shareModalTemplate}
        onClose={() => setShareModalTemplate(null)}
        onUpdate={loadAllData}
      />

      {/* Version History Panel */}
      <VersionHistoryPanel
        template={versionPanelTemplate}
        isOpen={!!versionPanelTemplate}
        onClose={() => setVersionPanelTemplate(null)}
        onRevert={handleVersionRevert}
      />
    </div>
  );
};

export default EmailTemplates;
